import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal
} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoDirective} from '@jsverse/transloco';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckIcon,
  CoinsIcon,
  HouseIcon,
  LifeBuoyIcon,
  LightbulbIcon,
  LucideAngularModule,
  MapPinIcon,
  MessagesSquareIcon,
  SaveIcon,
  ScanEyeIcon,
  ScanFaceIcon,
  SlidersHorizontalIcon,
  XIcon
} from 'lucide-angular';
import {Observable, Subject, take, tap} from 'rxjs';
import {TabsComponent} from '@shared/container/tabs/tabs.component';
import {ContactSupportComponent} from '@shared/content/contact-support/contact-support.component';
import {ActivatedRoute} from '@angular/router';
import {HostForm} from '@domain/host/component/form/host-editor/host-editor';
import {HostDTO, LegalForm, UpdateHostRequest} from '@domain/host/host.dto';
import {StringValidator} from '@shared/form/validator/string.validator';
import {
  HostAboutEditorComponent
} from '@domain/host/component/form/host-editor/host-about-editor/host-about-editor.component';
import {HostApi} from '@domain/host/host.api';
import {
  HostContactEditorComponent
} from '@domain/host/component/form/host-editor/host-contact-editor/host-contact-editor.component';
import {MediaDTO} from '@domain/media/media.dto';
import {HostCoverFieldComponent} from '@domain/host/component/form/host-cover-field/host-cover-field.component';
import {TabItemComponent} from '@shared/container/tabs/tab-item/tab-item.component';
import {TabsService} from '@shared/container/tabs/tabs.service';

export type HostTab = 'host' | 'contact';

@Component({
  selector: 'app-host-editor',
  providers: [TabsService],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
    LucideAngularModule,
    TabsComponent,
    TabItemComponent,
    ContactSupportComponent,
    HostAboutEditorComponent,
    HostContactEditorComponent,
    HostCoverFieldComponent
  ],
  templateUrl: './host-editor.component.html'
})
export class HostEditorComponent implements OnInit, AfterViewInit, OnDestroy {

  public static readonly QUERY_PARAM_TAB = 'tab';
  private static readonly TABS_ORDER = ['host', 'contact'] as HostTab[];

  private readonly tabsService = inject(TabsService);
  public readonly isFirstTab = computed(() => this.tabsService.isFirst());
  public readonly isLastTab = computed(() => this.tabsService.isLast());

  public readonly initialHost = input.required<HostDTO>();
  public readonly cancel = output<void>();
  public readonly ok = output<HostDTO>();
  public readonly saved = output<HostDTO>();

  public readonly newEmail = computed(() => this.initialHost().newEmail ?? null);

  public readonly supportEmailSubject = computed(() => `Contact au sujet de l'organisation ${this.initialHost().id}`);
  public readonly supportWhatsappMessage = computed(() => `Hopla! Pouvez-vous m'aider pour l'organisation ${this.initialHost().id}?`);


  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);

  public readonly hostForm: HostForm = this.fb.group({
    logo: this.fb.control<MediaDTO | null>(null),
    cover: this.fb.control<MediaDTO | null>(null),
    about: this.fb.group({
      name: this.fb.control<string | null>(null),
      legalForm: this.fb.control<LegalForm | null>(null),
      description: this.fb.control<string | null>(null)
    }),
    contact: this.fb.group({
      messaging: this.fb.control<boolean | null>(null),
      email: this.fb.control<string | null>(null),
      newEmail: this.fb.control<string | null>(null),
      webUrl: this.fb.control<string>('', {validators: [StringValidator.url()], updateOn: 'blur'}),
      fbUrl: this.fb.control<string>('', [
        Validators.pattern(/^https?:\/\/(www\.)?facebook\.com\/[A-Za-z0-9_.-]+\/?$/)
      ]),
      igUrl: this.fb.control<string>('', [
        Validators.pattern(/^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]+\/?$/)
      ])
    })
  });

  public readonly host = signal<HostDTO | null>(null);

  private readonly hostApi = inject(HostApi);

  public readonly icons = {
    idea: LightbulbIcon,
    host: HouseIcon,
    calendar: CalendarIcon,
    price: CoinsIcon,
    help: LifeBuoyIcon,
    contact: MessagesSquareIcon,
    map: MapPinIcon,
    audience: ScanFaceIcon,
    options: SlidersHorizontalIcon,
    note: LightbulbIcon,
    ok: CheckIcon,
    save: SaveIcon,
    previous: ArrowLeftIcon,
    next: ArrowRightIcon,
    cancel: XIcon,
    preview: ScanEyeIcon
  };

  public constructor() {
    effect(() => {
      const host = this.initialHost();
      this.hostForm.patchValue({
        cover: host.cover ?? null,
        about: {
          name: host.name ?? null,
          legalForm: host.legalForm ?? null,
          description: host.description ?? null
        },
        contact: {
          messaging: host.messaging ?? null,
          email: host.email ?? null,
          newEmail: host.newEmail ?? null,
          webUrl: host.webUrl ?? null,
          fbUrl: host.fbUrl ?? null,
          igUrl: host.igUrl ?? null
        }
      });
    });
  }

  public ngOnInit(): void {
    this.host.set(this.initialHost());
  }

  public ngAfterViewInit(): void {
    const queryTab = this.route.snapshot.queryParams[HostEditorComponent.QUERY_PARAM_TAB] as HostTab;
    const tabToSelect = HostEditorComponent.TABS_ORDER.indexOf(queryTab ?? 'host');
    this.tabsService.selectIndex(tabToSelect);
  }

  public save(): void {
    this.save$().subscribe(_ => {});
  }

  public goToPrevious(): void {
    this.tabsService.selectPrevious();
  }

  public goToNext(): void {
    this.tabsService.selectNext();
  }

  public saveAndClose(): void {
    this.save$().subscribe(newSchedule => {
      this.ok.emit(newSchedule);
    });
  }

  private save$(): Observable<HostDTO> {
    const payload = this.getPayload();
    const hostId = this.initialHost().id;
    return this.hostApi.update(hostId, payload).pipe(
      take(1),
      tap(savedHost => {
        this.saved.emit(savedHost);
      })
    );
  }

  protected getPayload(): Partial<UpdateHostRequest> {
    const value = this.hostForm.value;
    return {
      name: value.about?.name ?? null,
      legalForm: value.about?.legalForm ?? null,
      description: value.about?.description ?? null,

      messaging: value.contact?.messaging ?? null,
      newEmail: value.contact?.newEmail ?? null,
      webUrl: value.contact?.webUrl ?? null,
      fbUrl: value.contact?.fbUrl ?? null,
      igUrl: value.contact?.igUrl ?? null
    };
  }

  public cancelClick(): void {
    this.cancel.emit();
  }

  private readonly destroy$ = new Subject<void>();

  public ngOnDestroy(): void {
    this.destroy$.next();
  }
}
